import React, { useEffect } from "react";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "../../../../../../app/hooks";
import { FormInputs } from "../../../../../../types/walletTypes";
import { savedAsiaPayoutData } from "../../../../redux/formDataSlice";
import { CHINESE_NATIONAL_ID_RULES } from "../../../shared/helpers";
import {
  ContentWrapper,
  InputWrapper,
  ItemWrapper,
  Error,
  ButtonsWrapper,
} from "../../../shared/styles";
import { Input } from "../../../../../../components/common/Input";
import { InputPhone } from "../../../../../../components/common/InputPhone";
import CountrySelect from "../../../../../../components/common/CountrySelect";
import { Button } from "../../../../../../components/common/Button";
import Loader from "../../../../../../components/common/Loader";

interface AliPayBeneficiaryFormProps {
  handleNext?: () => void;
  handlePrevious?: () => void;
}

const AliPayBeneficiaryForm: React.FC<AliPayBeneficiaryFormProps> = ({
  handleNext,
  handlePrevious,
}) => {
  const dispatch = useAppDispatch();
  const asiaPayoutData = useAppSelector((state) => (state.formData as any)?.asiaPayoutData?.cn || {});
  const { loading } = useAppSelector((state) => state.wallets || {});

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors, isValid },
  } = useForm<FormInputs>({
    defaultValues: {
      country: "CN",
      ...asiaPayoutData,
    },
    mode: "onChange"
  });

  useEffect(() => {
    if (asiaPayoutData && Object.keys(asiaPayoutData).length > 0) {
      reset({
        country: "CN",
        ...asiaPayoutData,
      });
    }
  }, [asiaPayoutData, reset]);

  const onSubmit: SubmitHandler<FormInputs> = (data) => {
    const updatedData: FormInputs = {
      ...asiaPayoutData,
      ...data,
      country: "CN",
      receiverCountry: "CN",
    };
    dispatch(savedAsiaPayoutData({ country: "cn", data: updatedData }));
    handleNext?.();
  };

  return (
    <ContentWrapper>
      <Loader isLoading={loading} />

      <InputWrapper>
        <Controller
          name="accountHolderName"
          control={control}
          rules={{ required: "Name is required" }}
          render={({ field }) => (
            <Input
              type="text"
              label="Beneficiary name"
              value={field.value || ""}
              onChange={field.onChange}
              hasError={!!errors?.accountHolderName}
            />
          )}
        />
        {errors?.accountHolderName?.message && (
          <Error>{String(errors.accountHolderName.message)}</Error>
        )}
      </InputWrapper>

      <InputWrapper>
        <Controller
          name="receiverIdNumber"
          control={control}
          rules={CHINESE_NATIONAL_ID_RULES()}
          render={({ field }) => (
            <Input
              type="text"
              label="Chinese national ID number"
              value={field.value || ""}
              onChange={field.onChange}
              hasError={!!errors?.receiverIdNumber}
            />
          )}
        />
        {errors?.receiverIdNumber?.message && (
          <Error>{String(errors.receiverIdNumber.message)}</Error>
        )}
      </InputWrapper>

      <InputWrapper>
        <Controller
          name="receiverMobileNumber"
          control={control}
          rules={{ required: "Phone number is required" }}
          render={({ field }) => (
            <InputPhone
              label="Alipay phone number"
              field={field}
              defaultCountry="CN"
            />
          )}
        />
        {errors?.receiverMobileNumber?.message && (
          <Error>{String(errors.receiverMobileNumber.message)}</Error>
        )}
      </InputWrapper>

      <InputWrapper>
        <Controller
          name="receiverStreetAddress"
          control={control}
          rules={{ required: "Address is required" }}
          render={({ field }) => (
            <Input
              type="text"
              label="Address"
              value={field.value || ""}
              onChange={field.onChange}
              hasError={!!errors?.receiverStreetAddress}
            />
          )}
        />
        {errors?.receiverStreetAddress?.message && (
          <Error>{String(errors.receiverStreetAddress.message)}</Error>
        )}
      </InputWrapper>

      <ItemWrapper>
        <InputWrapper>
          <Controller
            name="receiverCity"
            control={control}
            rules={{ required: "City is required" }}
            render={({ field }) => (
              <Input
                type="text"
                label="City"
                value={field.value || ""}
                onChange={field.onChange}
                hasError={!!errors?.receiverCity}
              />
            )}
          />
          {errors?.receiverCity?.message && (
            <Error>{String(errors.receiverCity.message)}</Error>
          )}
        </InputWrapper>

        <InputWrapper>
          <Controller
            name="receiverState"
            control={control}
            rules={{ required: "State is required" }}
            render={({ field }) => (
              <Input
                type="text"
                label="State"
                value={field.value || ""}
                onChange={field.onChange}
                hasError={!!errors?.receiverState}
              />
            )}
          />
          {errors?.receiverState?.message && (
            <Error>{String(errors.receiverState.message)}</Error>
          )}
        </InputWrapper>
      </ItemWrapper>

      <ItemWrapper>
        <InputWrapper>
          <Controller
            name="country"
            control={control}
            render={({ field }) => (
              <CountrySelect
                value="CN"
                disabled={true}
                placeholder="Country"
                lightBorder
              />
            )}
          />
        </InputWrapper>

        <InputWrapper>
          <Controller
            name="receiverPostCode"
            control={control}
            rules={{ required: "Postal code is required" }}
            render={({ field }) => (
              <Input
                type="text"
                label="Postal code"
                value={field.value || ""}
                onChange={field.onChange}
                hasError={!!errors?.receiverPostCode}
              />
            )}
          />
          {errors?.receiverPostCode?.message && (
            <Error>{String(errors.receiverPostCode.message)}</Error>
          )}
        </InputWrapper>
      </ItemWrapper>

      <ButtonsWrapper>
        <Button onClick={handlePrevious} theme="alternate" height="48px" width="203px">
          Back
        </Button>
        <Button disabled={!isValid} onClick={handleSubmit(onSubmit)} label="Continue" height="48px" />
      </ButtonsWrapper>
    </ContentWrapper>
  );
};

export default AliPayBeneficiaryForm;
